import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, UserPlus, LogIn } from 'lucide-react';

const API = '/api';

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { token, role, username: userName, partner_id } = response.data;
      onLogin(token, role, userName, partner_id);
      toast.success('تم تسجيل الدخول بنجاح');
    } catch (error) {
      if (error.response) {
        // Server responded with error
        const detail = error.response.data?.detail;
        if (detail) {
          toast.error(detail);
        } else {
          toast.error(`خطأ من الخادم (${error.response.status})`);
        }
      } else if (error.request) {
        // No response received
        toast.error('لا يمكن الاتصال بالخادم - تحقق من اتصال الإنترنت');
      } else {
        toast.error('فشل تسجيل الدخول');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, { username, password, full_name: fullName });
      const { token, role, username: userName, partner_id } = response.data;
      onLogin(token, role, userName, partner_id);
      toast.success('تم إنشاء الحساب بنجاح!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إنشاء الحساب');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setFullName('');
  };

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-5xl font-black text-emerald-700 mb-2" style={{letterSpacing: '-0.02em'}}>
              افيوس جروب
            </h1>
            <p className="text-slate-500 text-base">نظام إدارة حسابات الشركاء والاستثمار</p>
          </div>

          {/* Toggle buttons */}
          <div className="flex mb-6 bg-slate-100 rounded-full p-1">
            <button
              onClick={() => { setIsRegister(false); resetForm(); }}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${!isRegister ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500'}`}
              data-testid="tab-login"
            >
              <LogIn className="inline h-4 w-4 ml-1" />
              تسجيل الدخول
            </button>
            <button
              onClick={() => { setIsRegister(true); resetForm(); }}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${isRegister ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-500'}`}
              data-testid="tab-register"
            >
              <UserPlus className="inline h-4 w-4 ml-1" />
              حساب جديد
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-xl font-bold mb-6 text-center text-slate-800">
              {isRegister ? 'إنشاء حساب مستثمر جديد' : 'تسجيل الدخول'}
            </h2>
            
            <form onSubmit={isRegister ? handleRegister : handleLogin} className="space-y-5">
              {isRegister && (
                <div className="space-y-2">
                  <Label className="text-sm font-bold">الاسم الكامل</Label>
                  <Input
                    data-testid="register-fullname"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="h-12 bg-slate-50 border-slate-200"
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-bold">اسم المستخدم</Label>
                <Input
                  data-testid="login-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-12 bg-slate-50 border-slate-200"
                  placeholder="أدخل اسم المستخدم"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-bold">كلمة المرور</Label>
                <div className="relative">
                  <Input
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 bg-slate-50 border-slate-200 pl-10"
                    placeholder={isRegister ? '6 أحرف على الأقل' : 'أدخل كلمة المرور'}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button data-testid="login-submit" type="submit" disabled={loading}
                className={`w-full h-12 text-white font-bold rounded-full shadow-lg transition-all ${isRegister ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {loading ? 'جاري المعالجة...' : isRegister ? 'إنشاء الحساب' : 'تسجيل الدخول'}
              </Button>
            </form>
          </div>

        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(5, 150, 105, 0.92), rgba(6, 78, 59, 0.92)), url('https://images.unsplash.com/photo-1655843803144-2f4ef2444ae6?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}>
        <div className="text-center text-white z-10">
          <h2 className="text-4xl sm:text-5xl font-black mb-4">استثمر بثقة</h2>
          <p className="text-lg opacity-90 mb-6">منصة متكاملة لإدارة استثماراتك بشفافية</p>
          <div className="flex gap-4 justify-center text-sm opacity-80">
            <span className="bg-white/20 px-4 py-2 rounded-full">شفافية كاملة</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">عوائد مجزية</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">متابعة دائمة</span>
          </div>
        </div>
      </div>
    </div>
  );
}
